import { createContext, useState, useContext, useEffect, useRef } from "react";
import { useSymphonyApiService } from "../../../hooks/useSymphonyApi";
import { useChat } from "./ChatContext";
import { useUserProfile } from "./UserProfileContext";
import { ToastContext } from "cai-fusion";
import { useSignalR } from "./SignalRContext";
import env from "../../../configs/envConfig";
import { debugLog } from "../../../configs/loggingConfig";

const AccessContext = createContext();

function AccessProvider({ children }) {

    const apiServiceClient = useSymphonyApiService("framework", "v3");
    const maestroServiceClient = useSymphonyApiService("maestro", "v3");

    // referenced context components
    const { chat, chatId } = useChat();
    const { userProfile } = useUserProfile();
    const { createErrorToast } = useContext(ToastContext);
    const { addMappingMethod } = useSignalR();

    // data
    const [chatMembers, setChatMembers] = useState([]);
    const [canMakeContributor, setCanMakeContributor] = useState((env.REACT_APP_USERS_W_ADD_CONTRIBUTOR ?? "").split(',').map(u => u.toLowerCase()).filter(x => x !== ""));
    const [membershipLevels, setMembershipLevels] = useState({
        1: "Manager",
        2: "Contribute",
        3: "View Only"
    })
    const [limitSharing, setLimitSharing] = useState([]);

    const [chatMembershipLevels, setChatMembershipLevels] = useState({
        "Owner": 0,
        "Manager": 1,
        "Contribute": 2,
        "View Only": 3
    })

    // statuses
    const [userChatMembership, setUserChatMembership] = useState(null);
    const [canEditSettings, setCanEditSettings] = useState(true);
    const [canSendMessage, setCanSendMessage] = useState(true);
    const [canShareChat, setCanShareChat] = useState(true);
    const [isChatOwner, setIsChatOwner] = useState(false);

    // refs for signalR
    const chatMembersRef = useRef();



    // Get the chat members of the current chat.
    const getChatMembers = async () => {
        try {
            let members = chat?.chatUserAccesses;
            debugLog.log("[ACCESS] Get chat members: ", members);
            if (!members) {
                setChatMembers([]);
            }
            else {
                setChatMembers(members);
            }

            let membership;
            if (members) {
                membership = members.find((x) => x.userProfileId === userProfile?.userProfileId);
            }

            if (membership) {
                setUserChatMembership(membership.membership);
            }
            else {
                //It should never be possible to get here, since the backend won't return the chat if the user doesn't have access, but including for security sake
                debugLog.error("User does not have access to this chat.");
            }
        }
        catch (error) {
            debugLog.error("Error getting chat members", error);
            // possible leaveChat
        }
        finally {
            return chatMembers;
        }

    }

    // Check if a user is a member of the current chat
    const isMember = async (user) => {
        debugLog.log("[SHARECHAT] " + user.identifier + " being checked for membership");
        await getChatMembers();
        const membership = chatMembers.find(
            (x) => x.userProfileId === user?.userProfileId
        );
        return membership;
    }

    // Search available users to add to the chat.
    const searchUsers = async (query) => {
        return await apiServiceClient.UserProfiles.searchUsers(query);
    }

    // Updates the chat access to any added or removed users.
    const shareChat = async (user, membership) => {
        if (!chatId) {
            debugLog.error("[SHARECHAT] Unable to get chat id");
            return;
        }

        if (user) {
            try {
                debugLog.log("[SHARECHAT] Sharing chat with a user.");
                let resp = await maestroServiceClient.Chats.shareChat(chatId, undefined, user.identifier, membership);
                setChatMembers([...chatMembers, resp]);
            } catch (e) {
                if (e.response && e.response.status === 400 && e.response.data && e.response.data.error) {
                    createErrorToast(e.response.data.error);
                } else if (e.response && e.response.data && e.response.data.error) {
                    createErrorToast(e.response.data.error.split(':')[1]);
                } else {
                    createErrorToast("Unable to Share Chat");
                }
            }
        }
    }

    const unshareChat = async (user) => {
        if (!chatId) {
            debugLog.error("[SHARECHAT] Unable to get chat id");
            return;
        }
        if (user) {
            debugLog.log("[SHARECHAT] Unsharing chat with a user.")
            let resp = await maestroServiceClient.Chats.unshareChat(chatId, user.userProfileId);
            setChatMembers(chatMembers.filter(x => x.userProfile.identifier != user.userProfile.identifier));
        }
    }

    // update access level for user in a chat
    const updateChatAccess = async (user, accessLevel) => {
        debugLog.log("[SHARECHAT] updating chat access of " + user.identifier + " to " + accessLevel);
        if (user) {
            let ind = chatMembers.findIndex(
                (x) => x.userProfileId === user?.userProfileId
            );
            const newChatMembers = [...chatMembers];
            newChatMembers[ind].membership = accessLevel;
            setChatMembers(newChatMembers);
            await maestroServiceClient.Chats.updateChatAccess(chatId, accessLevel, user.userProfileId, user.identifier);
        }
    }

    const handleUserLeftChat = (leavingUserProfileId) => {
        debugLog.log("[ACCESS] User left chat. UserProfileId:", leavingUserProfileId);

        if (leavingUserProfileId) {
            // Filter out the user who left from chatMembers
            setChatMembers(prev => prev.filter(
                member => member.userProfileId !== leavingUserProfileId
            ));
        }
    }

    useEffect(() => {
        if (canMakeContributor.length === 0 || canMakeContributor.includes(userProfile?.identifier?.toLowerCase())) {
            setMembershipLevels({
                3: "View Only",
                2: "Contribute"
            })
        }

    }, [userProfile, canMakeContributor])

    useEffect(() => {
        // everything readers can do/default
        if (userChatMembership <= 3) {
            setCanEditSettings(false);
            setCanSendMessage(false);
        }
        // everything contributors can do
        if (userChatMembership <= 2) {
            setCanSendMessage(true);
        }
        // everything managers can do
        if (userChatMembership <= 1) {

        }
        // everything owners can do
        if (userChatMembership === 0) {
            setIsChatOwner(true);
            setCanEditSettings(true);
        }
    }, [userChatMembership])

    useEffect(() => {
        if (userChatMembership === 0 && (limitSharing.length === 0 || limitSharing.includes(userProfile?.identifier))) {
            setCanShareChat(true);
        } else if (canShareChat) {
            setCanShareChat(false);
        }
    }, [userProfile, userChatMembership, limitSharing])

    useEffect(() => {
        if (chat && userProfile) getChatMembers();
    }, [chat, userProfile])

    useEffect(() => {
        chatMembersRef.current = chatMembers;
    }, [chatMembers]);

    useEffect(() => {
        addMappingMethod({
            TextName: "UserLeftChat",
            Method: handleUserLeftChat,
        });
    }, []);

    return (
        <AccessContext.Provider
            value={{
                // States
                isChatOwner,
                chatMembers,
                setChatMembers,
                canMakeContributor,
                canEditSettings,
                canSendMessage,
                canShareChat,
                membershipLevels,

                // Functions
                isMember,
                getChatMembers,
                searchUsers,
                shareChat,
                unshareChat,
                updateChatAccess
            }}
        >
            {children}
        </AccessContext.Provider>
    );
}

// Hook to use the AccessContext in a component
function useAccess() {
    const context = useContext(AccessContext);
    if (context === undefined) {
        throw new Error(
            "useAccess must be used within a AccessProvider"
        );
    }
    return context;
}

export { AccessProvider, useAccess };
